import {
  MessageOutlined,
  PhoneOutlined,
  RobotOutlined,
  MobileOutlined,
  MailOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { t } from 'i18next';
import type { ParseKeys } from 'i18next';
import type { ComponentType } from 'react';
import type { ConversationType } from '@/services/webchat-service/webchat.service.types';

// Lookup table of icon + display label key keyed by STI type. Used by both the
// Command Center session card (Live tab) and the history table (History tab)
// so a rep instantly sees what kind of conversation it is. Adding a new STI
// type to the dashboard means adding a row here AND extending the
// COMMAND_CENTER_TYPES constant on the Rails side — keep them in lockstep.
type ChannelDescriptor = {
  label: string;
  icon: ComponentType;
  // Antd Tag color hint. Default tones picked to match the existing palette
  // already used elsewhere in the dashboard for AI/translation badges.
  color: string;
};

// The table holds a dictionary key, not the label: `t()` at module level would run once at
// import. getConversationChannel translates at call time, so callers still get a plain label.
type ChannelEntry = Omit<ChannelDescriptor, 'label'> & { labelKey: ParseKeys<'conversations'> };

const DESCRIPTORS: Record<ConversationType, ChannelEntry> = {
  ChatConversation: { labelKey: 'channels.ChatConversation', icon: MessageOutlined, color: 'blue' },
  ElevenLabsCall: { labelKey: 'channels.ElevenLabsCall', icon: RobotOutlined, color: 'purple' },
  AnalyticsCall: { labelKey: 'channels.AnalyticsCall', icon: PhoneOutlined, color: 'green' },
  AgentCall: { labelKey: 'channels.AgentCall', icon: UserOutlined, color: 'cyan' },
  TextConversation: { labelKey: 'channels.TextConversation', icon: MobileOutlined, color: 'gold' },
  EmailConversation: {
    labelKey: 'channels.EmailConversation',
    icon: MailOutlined,
    color: 'magenta',
  },
};

const FALLBACK: ChannelEntry = {
  labelKey: 'channels.unknown',
  icon: MessageOutlined,
  color: 'default',
};

export function getConversationChannel(type: string | undefined | null): ChannelDescriptor {
  const { labelKey, ...rest } = (type && DESCRIPTORS[type as ConversationType]) || FALLBACK;
  return { ...rest, label: t(`conversations:${labelKey}`) };
}
